import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getClassProgramLive, getClassProgramShowcase } from '@/api/classPrograms';

const itemKey = (item) => `${item.seat_number}:${item.slot}`;

export function shuffledReadyKeys(items, random = Math.random) {
  const keys = items.filter((item) => item.state === 'ready').map(itemKey);
  for (let index = keys.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(random() * (index + 1));
    [keys[index], keys[swap]] = [keys[swap], keys[index]];
  }
  return keys;
}

export function ShowcasePlaylist() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const playAfterChangeRef = useRef(false);
  const [program, setProgram] = useState(null);
  const [items, setItems] = useState([]);
  const [order, setOrder] = useState([]);
  const [currentKey, setCurrentKey] = useState('');
  const [shuffle, setShuffle] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [tapToPlay, setTapToPlay] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    Promise.all([getClassProgramLive(programId), getClassProgramShowcase(programId)])
      .then(([live, rows]) => {
        if (!active) return;
        const ready = rows.filter((item) => item.state === 'ready').map(itemKey);
        setProgram(live.program); setItems(rows); setOrder(ready); setCurrentKey(ready[0] || '');
      })
      .catch((requestError) => { if (active) setError(requestError.message); });
    return () => { active = false; };
  }, [programId]);

  const byKey = useMemo(() => new Map(items.map((item) => [itemKey(item), item])), [items]);
  const current = byKey.get(currentKey) || null;
  const position = Math.max(0, order.indexOf(currentKey));

  const attemptPlay = useCallback(async () => {
    if (!audioRef.current || !current) return;
    setTapToPlay(false);
    try { await audioRef.current.play(); setPlaying(true); }
    catch (_) { setPlaying(false); setTapToPlay(true); }
  }, [current]);

  useEffect(() => {
    if (!playAfterChangeRef.current || !current) return;
    playAfterChangeRef.current = false;
    audioRef.current?.load();
    attemptPlay();
  }, [attemptPlay, current]);

  const move = (delta, keepPlaying = false) => {
    if (!order.length) return;
    const next = (position + delta + order.length) % order.length;
    playAfterChangeRef.current = keepPlaying || playing;
    setTapToPlay(false); setCurrentKey(order[next]);
  };

  const toggleShuffle = () => {
    const next = !shuffle;
    setShuffle(next);
    const nextOrder = next ? shuffledReadyKeys(items) : items.filter((item) => item.state === 'ready').map(itemKey);
    setOrder(nextOrder);
    if (!nextOrder.includes(currentKey)) setCurrentKey(nextOrder[0] || '');
  };

  if (error) return <main className="showcase-player showcase-player-message" data-skin="playground"><p role="alert">{error}</p><button onClick={() => navigate(`/facilitator/class/${programId}`)}>Back to live view</button></main>;
  if (!program) return <main className="showcase-player showcase-player-message" data-skin="playground"><p>Loading the showcase...</p></main>;

  return <main className="showcase-player" data-skin="playground">
    <header className="showcase-player-header">
      <button onClick={() => navigate(`/facilitator/class/${programId}`)}>Back</button>
      <div><span>Showcase playlist</span><h1>{program.name}</h1></div>
      <label><input type="checkbox" checked={shuffle} onChange={toggleShuffle} />Shuffle</label>
      <button onClick={() => navigate(`/facilitator/class/${programId}/projector`)}>Projector mode</button>
    </header>
    {current ? <section className="showcase-now" aria-live="polite">
      <span>Now playing · Seat {current.seat_number}</span>
      <h2>{current.display_name || `Seat ${current.seat_number}`}</h2>
      <p>{current.title}</p>
      <audio ref={audioRef} src={current.stream_url} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => move(1, true)} />
      {tapToPlay && <button className="showcase-tap" onClick={attemptPlay}>Tap to play</button>}
      <div className="showcase-controls">
        <button onClick={() => move(-1)} aria-label="Previous beat">Previous</button>
        <button className="primary" onClick={() => {
          if (playing) { audioRef.current?.pause(); setPlaying(false); }
          else attemptPlay();
        }}>{playing ? 'Pause' : 'Play'}</button>
        <button onClick={() => move(1)} aria-label="Next beat">Next</button>
      </div>
      <small>{order.length ? position + 1 : 0} of {order.length} saved beats</small>
    </section> : <section className="showcase-now"><img src="/pizzul/thinking.png" alt="Pizzul" /><h2>No beats saved yet</h2><p>Turn on Showcase time, then kids can tap Take it home.</p></section>}
    <section className="showcase-list" aria-label="Class beats">
      <h2>Class beats</h2>
      <div>{items.map((item) => <article key={itemKey(item)} className={itemKey(item) === currentKey ? 'current' : ''}>
        <span>Seat {item.seat_number} · Beat {item.slot}</span>
        <strong>{item.display_name || `Seat ${item.seat_number}`}</strong>
        <p>{item.title}</p>
        <b>{item.state === 'ready' ? 'Saved' : 'Not saved yet'}</b>
      </article>)}</div>
    </section>
  </main>;
}

export default ShowcasePlaylist;
